import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { SelectOption, StyleObj } from '../../@types';
import { CasinoMarketplace, CasinoMarketplaceEditValues } from '../../@types/casino';
import { useToast } from '../../contexts/ToastContext';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';
import { useCasinoCurrencies, useCasinoMarketplace, useUpdateCasinoMarketplace } from '../../queries/casino';
import { CasinoMarketplaceFormData, casinoMarketplaceFormSchema } from '../../schema';
import FormSelect from '../molecules/FormSelect';

const AGGREGATOR_OPTIONS: SelectOption[] = [
  { id: 'GOLDGAMES', name: 'Gold Games' },
  { id: 'GGA', name: 'GGA' },
];

const GAME_TYPE_OPTIONS: SelectOption[] = [
  { id: 'Originals', name: 'Originals' },
  { id: 'Copies', name: 'Copies' },
];

const DEFAULT_FORM_DATA: CasinoMarketplaceFormData = {
  name: '',
  currency: '',
  language: '',
  aggregators: [],
  freeSpinsAggregator: '',
  gameType: [],
  apiDomain: '',
  url: '',
  slug: '',
  minimumStakePerBet: '',
  maximumStakePerBet: '',
};

const styles: StyleObj = {
  page: {
    width: '100%',
    maxWidth: 960,
    mx: 'auto',
    p: { xs: 2, md: 3 },
  },
  card: {
    p: { xs: 2, md: 3 },
    borderRadius: 2,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
    gap: 2,
  },
  fullWidth: {
    gridColumn: { xs: 'auto', md: '1 / -1' },
  },
  actions: {
    justifyContent: 'flex-end',
  },
  loader: {
    width: '100%',
    minHeight: 240,
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const toFormValues = (marketplace?: CasinoMarketplace): CasinoMarketplaceFormData => {
  const legacyAggregator = marketplace?.aggregator ?? marketplace?.activeAggregator;
  const gameType =
    marketplace?.gameType === 'Both' ? ['Originals', 'Copies'] : marketplace?.gameType ? [marketplace.gameType] : [];

  return {
    name: marketplace?.name ?? '',
    currency: marketplace?.currency ?? '',
    language: marketplace?.language ?? '',
    aggregators:
      marketplace?.aggregators ?? marketplace?.activeAggregators ?? (legacyAggregator ? [legacyAggregator] : []),
    freeSpinsAggregator: marketplace?.freeSpinsAggregator ?? '',
    gameType,
    apiDomain: marketplace?.apiDomain ?? '',
    url: marketplace?.url ?? '',
    slug: marketplace?.slug ?? '',
    minimumStakePerBet: String(marketplace?.minimumStakePerBet ?? ''),
    maximumStakePerBet: String(marketplace?.maximumStakePerBet ?? ''),
  };
};

const toUpdatePayload = (values: CasinoMarketplaceFormData): CasinoMarketplaceEditValues => ({
  ...values,
  freeSpinsAggregator: values.aggregators.length === 1 ? values.aggregators[0] : values.freeSpinsAggregator,
  gameType: values.gameType.length > 1 ? 'Both' : values.gameType[0],
  minimumStakePerBet: Number(values.minimumStakePerBet),
  maximumStakePerBet: Number(values.maximumStakePerBet),
});

const getAggregatorLabel = (value: string) =>
  AGGREGATOR_OPTIONS.find((option) => String(option.id) === value)?.name ?? value;

const getGameTypeLabel = (value: string) =>
  GAME_TYPE_OPTIONS.find((option) => String(option.id) === value)?.name ?? value;

const CasinoMarketplacePage = () => {
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();
  const {
    data: marketplace,
    isLoading: isMarketplaceLoading,
    isFetching: isMarketplaceFetching,
    isError: isMarketplaceError,
  } = useCasinoMarketplace();
  const { data: currencies = [], isLoading: isCurrenciesLoading } = useCasinoCurrencies();
  const updateMarketplaceMutation = useUpdateCasinoMarketplace();

  const {
    control,
    register,
    reset,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CasinoMarketplaceFormData>({
    defaultValues: DEFAULT_FORM_DATA,
    resolver: zodResolver(casinoMarketplaceFormSchema),
  });

  const aggregators = useWatch({ control, name: 'aggregators' });
  const needsFreeSpinsChoice = aggregators.includes('GGA') && aggregators.includes('GOLDGAMES');

  useEffect(() => {
    reset(toFormValues(marketplace));
  }, [marketplace, reset]);

  const currencyOptions = useMemo(
    () =>
      currencies.map((currency) => ({
        id: currency.code,
        name: `${currency.symbolNative ?? ''}${currency.code}`,
      })),
    [currencies]
  );

  const onSubmit = (values: CasinoMarketplaceFormData) => {
    if (!marketplace?._id) return;

    const data = toUpdatePayload(values);

    updateMarketplaceMutation.mutate(
      { marketplaceId: marketplace._id, data },
      {
        onSuccess: (response) => {
          reset(toFormValues(response.newMarketplaceData ?? { ...marketplace, ...data }));
          enqueueSnackbar({ message: 'Update success!', variant: 'success' });
        },
        onError: (error) => handleErrorMessage(error),
      }
    );
  };

  const isLoading = isMarketplaceLoading || isCurrenciesLoading || (isMarketplaceFetching && !marketplace);

  if (isLoading) {
    return (
      <Stack sx={styles.loader}>
        <CircularProgress />
      </Stack>
    );
  }

  if (isMarketplaceError) {
    return (
      <Stack sx={styles.loader}>
        <Typography color='error'>Failed to load marketplace data.</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={styles.page}>
      <Paper variant='outlined' sx={styles.card}>
        <Stack component='form' spacing={3} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Typography variant='h4'>Edit marketplace</Typography>

          <Box sx={styles.grid}>
            <TextField
              label='Marketplace name'
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
              required
              sx={styles.fullWidth}
            />
            <FormSelect
              name='currency'
              label='Currency'
              control={control}
              options={currencyOptions}
              error={errors.currency}
              required
              allowDeselect={false}
            />
            <TextField
              label='Language'
              {...register('language')}
              error={!!errors.language}
              helperText={errors.language?.message}
              required
            />
            <FormControl fullWidth required error={!!errors.aggregators}>
              <InputLabel id='select-aggregators-label'>Aggregator</InputLabel>
              <Controller
                control={control}
                name='aggregators'
                render={({ field: { onChange, value } }) => {
                  const selectedAggregators = Array.isArray(value) ? value : [];

                  return (
                    <Select<string[]>
                      multiple
                      labelId='select-aggregators-label'
                      id='select-aggregators'
                      label='Aggregator'
                      value={selectedAggregators}
                      onChange={(event: SelectChangeEvent<string[]>) => {
                        const nextValue = event.target.value;
                        onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
                      }}
                      renderValue={(selected) => selected.map(getAggregatorLabel).join(', ')}
                    >
                      {AGGREGATOR_OPTIONS.map((option) => (
                        <MenuItem key={option.id} value={String(option.id)}>
                          <Checkbox checked={selectedAggregators.includes(String(option.id))} />
                          <ListItemText primary={option.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  );
                }}
              />
              <FormHelperText>{errors.aggregators?.message}</FormHelperText>
            </FormControl>
            {needsFreeSpinsChoice && (
              <FormSelect
                control={control}
                name='freeSpinsAggregator'
                label='Free Spins Aggregator'
                options={AGGREGATOR_OPTIONS}
                required
                allowDeselect={false}
                closeOnSelect
                error={errors.freeSpinsAggregator}
              />
            )}
            <FormControl fullWidth required error={!!errors.gameType}>
              <InputLabel id='select-game-type-label'>Games Type</InputLabel>
              <Controller
                control={control}
                name='gameType'
                render={({ field: { onChange, value } }) => {
                  const selectedGameTypes = Array.isArray(value) ? value : [];

                  return (
                    <Select<string[]>
                      multiple
                      labelId='select-game-type-label'
                      id='select-game-type'
                      label='Games Type'
                      value={selectedGameTypes}
                      onChange={(event: SelectChangeEvent<string[]>) => {
                        const nextValue = event.target.value;
                        onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
                      }}
                      renderValue={(selected) => selected.map(getGameTypeLabel).join(', ')}
                    >
                      {GAME_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.id} value={String(option.id)}>
                          <Checkbox checked={selectedGameTypes.includes(String(option.id))} />
                          <ListItemText primary={option.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  );
                }}
              />
              <FormHelperText>{errors.gameType?.message}</FormHelperText>
            </FormControl>
            <TextField
              label='Api domain'
              {...register('apiDomain')}
              error={!!errors.apiDomain}
              helperText={errors.apiDomain?.message}
              required
            />
            <TextField
              label='Url'
              {...register('url')}
              error={!!errors.url}
              helperText={errors.url?.message}
              required
            />
            <TextField
              label='Slug'
              {...register('slug')}
              error={!!errors.slug}
              helperText={errors.slug?.message}
              required
              sx={styles.fullWidth}
            />
            <TextField
              label='Minimum Stake Per Bet'
              {...register('minimumStakePerBet')}
              error={!!errors.minimumStakePerBet}
              helperText={errors.minimumStakePerBet?.message}
              required
            />
            <TextField
              label='Maximum Stake Per Bet'
              {...register('maximumStakePerBet')}
              error={!!errors.maximumStakePerBet}
              helperText={errors.maximumStakePerBet?.message}
              required
            />
          </Box>

          <Stack direction='row' sx={styles.actions}>
            <Button type='submit' variant='contained' disabled={!isDirty || updateMarketplaceMutation.isPending}>
              Save Changes
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CasinoMarketplacePage;
